import type { ReactNode } from 'react';
import {
  Kanban,
  KanbanCardBody,
  KanbanTag,
  NotProvided,
  type KanbanColor,
  type KanbanStage,
} from '@evogard/ui';
import type { ClaimSummary } from '@evogard/contract';
import {
  ANALYSIS_STAGE_COLORS,
  COMPENSATION_STAGE_COLORS,
  COMPENSATION_WORK_STAGES,
  REPAIR_STAGE_COLORS,
  ANALYSIS_WORK_STAGES,
  REPAIR_WORK_STAGES,
  analysisHoldLabel,
  cafStatusLabel,
  cafStatusNeedsAction,
  formatInstant,
  type ClaimDepartment,
} from '@evogard/domain';
import { useCollapsedStages } from '../../../lib/collapsed-stages.js';
import { useSessionContext } from '../../../lib/session.js';
import { deskOf } from './desk.js';
import { CLAIM_TYPE_OPTIONS, DEPARTMENT_LABEL, labelOf, phaseLabel } from './labels.js';
import { fetchClaimPhasePage, type ClaimBoardFilter } from './useClaims.js';
import { ClaimLabelTags } from './ClaimLabels.js';
import { ClaimDeadlineTags } from './ClaimDeadlines.js';
import { useSlaSummary } from './useDeadlines.js';

/** Quantos cartões cada coluna pede por vez. */
const PAGE_SIZE = 50;

/** Os departamentos que têm fase — os três, desde a Indenização (09/10/2026). */
export type PhasedDepartment = ClaimDepartment;

/**
 * As colunas de cada departamento, na ordem do pipe. Vêm do código
 * (`@evogard/domain`), e não de quem usa: o quadro não cria, não renomeia e
 * não arquiva fase.
 *
 * Na Análise são só as ETAPAS DE TRABALHO (decisão do dono em 06/10/2026): a
 * situação e a parada são selo no cartão, e o sinistro encerrado — num
 * desfecho — sai do quadro e fica na tabela.
 */
/** A cor de uma etapa no quadro e na tabela; `undefined` desenha o cinza de quem não tem. */
export function stageColorOf(department: PhasedDepartment, code: string) {
  return (STAGE_COLORS[department] as Record<string, KanbanColor | undefined>)[code];
}

/** A paleta de cada departamento, do código (`@evogard/domain`). */
const STAGE_COLORS: Record<PhasedDepartment, Readonly<Record<string, KanbanColor>>> = {
  analise: ANALYSIS_STAGE_COLORS,
  reparo: REPAIR_STAGE_COLORS,
  indenizacao: COMPENSATION_STAGE_COLORS,
};

export const DEPARTMENT_STAGES: Record<PhasedDepartment, readonly string[]> = {
  analise: ANALYSIS_WORK_STAGES,
  reparo: REPAIR_WORK_STAGES,
  indenizacao: COMPENSATION_WORK_STAGES,
};

/**
 * Os sinistros de um departamento em quadro — uma coluna por fase, lida de
 * `claims.current_phase`, que é onde o sinistro está (o departamento não tem
 * fase própria).
 *
 * ## Só leitura, por enquanto
 *
 * Nada se arrasta: mudar de fase na Análise grava o formulário da fase, que o
 * arrastar não preenche, e passar da Análise para o Reparo é abrir um reparo,
 * não mudar de coluna. Também não há ordem dentro da fase para o arrastar
 * respeitar. Quem decide o que o clique abre é a tela.
 *
 * Não é `<Page>`: mora dentro da tela de rota do departamento.
 */
export function ClaimsBoard({
  department,
  phase,
  filter,
  toolbar,
  onOpen,
  version = 0,
  columns,
}: {
  department: PhasedDepartment;
  /**
   * A fase escolhida no filtro: a coluna dela aberta, e as outras recolhidas
   * em faixa (28/10/2026). Até então as outras sumiam.
   */
  phase?: string;
  filter: ClaimBoardFilter;
  toolbar: ReactNode;
  onOpen: (claim: ClaimSummary) => void;
  /** Sobe a cada movimento da mesa: o cartão muda de coluna sem recarregar a tela. */
  version?: number;
  /** As colunas que esta pessoa vê — sem elas, todas as etapas do departamento. */
  columns?: readonly string[];
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  /* As colunas são sempre as etapas: a fase do filtro só decide quais ficam
     abertas. Uma fase que não é etapa (um desfecho) ainda vira a coluna única,
     como antes — o filtro do departamento não a oferece, mas o tipo aceita. */
  const all = columns ?? DEPARTMENT_STAGES[department];
  const codes = phase && !all.includes(phase) ? [phase] : all;
  const { collapsed, onCollapse } = useCollapsedStages(
    `claims.${department}`,
    codes,
    phase,
  );
  /* Quantos estão com o SLA expirado em cada fase — o selo do cabeçalho. */
  const sla = useSlaSummary(department, version);
  const stages: KanbanStage[] = codes.map((code) => {
    const expired = sla.data?.find((s) => s.phase === code)?.slaExpired ?? 0;
    return {
      id: code,
      name: phaseLabel(department, code),
      /* A cor do cabeçalho da coluna (07/10/2026) — só a Análise tem as suas. */
      color: stageColorOf(department, code),
      ...(expired > 0 && {
        alert: expired === 1 ? '1 expirado' : `${expired} expirados`,
      }),
    };
  });

  return (
    <Kanban<ClaimSummary>
      label={`Quadro de sinistros ${deskOf(department)?.of ?? DEPARTMENT_LABEL[department]}`}
      stages={stages}
      toolbar={toolbar}
      cardId={(c) => String(c.id)}
      cardLabel={(c) => `Sinistro #${c.id}`}
      loadPage={async (phase, cursor) => {
        const page = cursor === null ? 1 : Number(cursor);
        const result = await fetchClaimPhasePage(
          department,
          phase,
          filter,
          page,
          PAGE_SIZE,
        );
        return {
          items: result.items,
          next: page * PAGE_SIZE < result.total ? String(page + 1) : null,
          total: result.total,
        };
      }}
      /* A faixa recolhida pede só o total: uma página de um sinistro. */
      countStage={async (phase) =>
        (await fetchClaimPhasePage(department, phase, filter, 1, 1)).total
      }
      collapsed={collapsed}
      onCollapse={onCollapse}
      scrollToStage={phase}
      /* Filtro, departamento ou praça novos são recorte novo: as colunas
         recomeçam da primeira página. A fase do filtro não entra — ela não
         recorta os sinistros, só abre uma coluna. */
      reloadKey={JSON.stringify([tenantId, department, filter, version])}
      renderCard={(c) => <ClaimCardBody claim={c} />}
      onOpenCard={onOpen}
      emptyColumnText="Nenhum sinistro nesta fase."
    />
  );
}

function ClaimCardBody({ claim: c }: { claim: ClaimSummary }) {
  /* O associado vem do espelho do SGA, e pode não vir: com a esteira atrasada
     o cartão continua, sem o nome. */
  const name = c.type === 'terceiro' ? c.thirdParty?.name : c.vehicle?.memberName;
  const plate = c.type === 'terceiro' ? c.thirdParty?.plate : c.vehicle?.plate;
  return (
    <KanbanCardBody
      tags={
        <>
          <KanbanTag tone={c.type === 'terceiro' ? 'progresso' : 'neutro'}>
            {c.type === 'terceiro' ? `Terceiro do #${c.parentClaimId}` : 'Associado'}
          </KanbanTag>
          {c.type === 'associado' && c.involvesThirdParty && (
            <KanbanTag>com terceiro</KanbanTag>
          )}
          {c.archivedAt && <KanbanTag>Arquivado</KanbanTag>}
          {/* A situação e a parada dentro da etapa — selo, e não coluna (06/10/2026). */}
          {c.hold && (
            <KanbanTag tone="alerta">Parado: {analysisHoldLabel(c.hold)}</KanbanTag>
          )}
          {/* O resultado da CAF (16/10/2026): o que pede alguém da casa em vermelho. */}
          {c.cafStatus && c.cafStatus !== 'em_processamento' && (
            <KanbanTag
              tone={
                cafStatusNeedsAction(c.cafStatus)
                  ? 'erro'
                  : c.cafStatus === 'aprovado'
                    ? 'sucesso'
                    : 'alerta'
              }
            >
              {cafStatusLabel(c.cafStatus)}
            </KanbanTag>
          )}
          {c.situation && (
            <KanbanTag tone="progresso">
              {deskOf(c.currentDepartment)?.situationLabel(c.situation) ?? c.situation}
            </KanbanTag>
          )}
          {/* As etiquetas dos setores de quem olha (07/10/2026). */}
          <ClaimLabelTags labels={c.labels} />
        </>
      }
      title={
        <>
          <span className="tabular-nums">#{c.id}</span> · {name ?? <NotProvided />}
          <span className="block text-xs font-normal text-texto-suave">
            {labelOf(CLAIM_TYPE_OPTIONS, c.claimType)}
            {plate && (
              <>
                {' · '}
                <span className="font-mono tabular-nums">{plate}</span>
              </>
            )}
          </span>
        </>
      }
      /* O responsável do departamento onde o card está — na Análise, o
         Analista Responsável (07/10/2026). */
      owner={{
        name: c.assignee?.name ?? null,
        emptyLabel: deskOf(c.currentDepartment)?.noAssignee ?? 'Sem responsável',
      }}
      detail={formatInstant(c.occurredAt)}
      /* O calendário é do vencimento, e o relógio, do SLA (07/10/2026): o
         ocorrido fica com o ícone de histórico. */
      detailIcon="historico"
      alerts={c.dueAt || c.phaseDeadlineAt ? <ClaimDeadlineTags claim={c} /> : undefined}
    />
  );
}
